import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Globe, LogOut, User, Heart, Clock, CreditCard, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/LanguageContext";
import { langNames, langFlags } from "@/lib/i18n";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const navLinks = user
    ? [
        { to: "/generate", icon: Sparkles, label: t("generate") },
        { to: "/favorites", icon: Heart, label: t("favorites") },
        { to: "/history", icon: Clock, label: t("history") },
        { to: "/pricing", icon: CreditCard, label: t("pricing") },
      ]
    : [
        { to: "/generate", icon: Sparkles, label: t("generate") },
      ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate("/");
    setProfileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0C0A09]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/rz0qyx4s_Firefly_GeminiFlash%202.png"
              alt="What To Cook Logo"
              className="w-9 h-9 rounded-lg transition-transform group-hover:rotate-12"
            />
            <span className="font-heading text-xl font-semibold text-white tracking-tight">
              {t("appName")}
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.slice(1)}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "bg-amber-500/10 text-amber-500"
                    : "text-stone-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => { setLangOpen(!langOpen); setProfileOpen(false); }}
                data-testid="language-selector"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-stone-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{langFlags[lang]}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-[#1C1917] border border-white/10 rounded-xl shadow-2xl py-1 z-50">
                  {Object.entries(langNames).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => { setLang(code); setLangOpen(false); }}
                      data-testid={`lang-${code}`}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        lang === code
                          ? "bg-amber-500/10 text-amber-500"
                          : "text-stone-300 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <span className="mr-2">{langFlags[code]}</span>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User / Auth */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => { setProfileOpen(!profileOpen); setLangOpen(false); }}
                  data-testid="profile-menu-button"
                  className="flex items-center gap-2"
                >
                  <div className="w-9 h-9 rounded-full bg-[#1C1917] border border-amber-500/30 flex items-center justify-center">
                    <span className="text-sm font-semibold text-amber-500">{initials}</span>
                  </div>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-[#1C1917] border border-white/10 rounded-xl shadow-2xl py-1 z-50">
                    <div className="px-4 py-3 border-b border-white/5">
                      <p className="text-sm font-medium text-white truncate">{user.name}</p>
                      <p className="text-xs text-stone-500 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setProfileOpen(false)}
                      data-testid="nav-profile"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-stone-300 hover:bg-white/5 hover:text-white"
                    >
                      <User className="w-4 h-4" />
                      {t("profile")}
                    </Link>
                    <button
                      onClick={handleLogout}
                      data-testid="logout-button"
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10"
                    >
                      <LogOut className="w-4 h-4" />
                      {t("logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="px-4 py-2 text-sm font-medium text-stone-300 hover:text-white transition-colors"
                >
                  {t("login")}
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className="px-4 py-2 text-sm font-semibold bg-amber-500 text-stone-950 rounded-full hover:bg-amber-600 transition-colors"
                >
                  {t("register")}
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-button"
              className="md:hidden p-2 text-stone-400 hover:text-white"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#0C0A09]/95 backdrop-blur-xl">
          <div className="px-4 py-4 space-y-1">
            {user ? (
              <>
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${
                      isActive(link.to)
                        ? "bg-amber-500/10 text-amber-500"
                        : "text-stone-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-stone-400 hover:text-white hover:bg-white/5"
                >
                  <User className="w-4 h-4" />
                  {t("profile")}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-3 text-sm font-medium text-stone-300 hover:text-white"
                >
                  {t("login")}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-3 text-sm font-semibold text-amber-500"
                >
                  {t("register")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
